/**
 * Upgrade dialog (M22).
 *
 * Wraps <PlanComparisonTable/> in a Radix Dialog and provides the
 * "Continue to checkout" CTA. Clicking the CTA navigates the user to the
 * existing /subscription page where the Razorpay flow already lives — for
 * the Enterprise tier we link to a "Contact sales" mailto.
 */
import { ExternalLink } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { PlanComparisonTable, type PlanKey } from './PlanComparisonTable';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan?: PlanKey;
}

export function UpgradeDialog({ open, onOpenChange, currentPlan }: UpgradeDialogProps): ReactNode {
  const navigate = useNavigate();
  const [pendingPlan, setPendingPlan] = useState<{
    plan: PlanKey;
    cycle: 'monthly' | 'annual';
  } | null>(null);

  function handleSelect(plan: PlanKey, cycle: 'monthly' | 'annual'): void {
    setPendingPlan({ plan, cycle });
  }

  function handleContinue(): void {
    if (!pendingPlan) {
      onOpenChange(false);
      void navigate('/subscription');
      return;
    }

    if (pendingPlan.plan === 'enterprise') {
      // No self-serve checkout for enterprise — surface a contact-sales hint.
      window.location.href = 'mailto:sales@hrms.example?subject=Enterprise%20plan%20enquiry';
      onOpenChange(false);
      return;
    }

    onOpenChange(false);
    void navigate('/subscription');
  }

  const ctaLabel = pendingPlan?.plan === 'enterprise' ? 'Contact sales' : 'Continue to checkout';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Upgrade your plan</DialogTitle>
          <DialogDescription>
            Choose the plan that fits your team. You can switch or cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          <PlanComparisonTable
            {...(currentPlan !== undefined && { currentPlan })}
            onSelectPlan={handleSelect}
          />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
          <Button onClick={handleContinue}>
            {ctaLabel}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
